import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, mergeProps as _mergeProps, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "row mb-3" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "col" }, [
    /*#__PURE__*/_createElementVNode("h2", null, "Trouble Report Tool")
  ])
], -1)
const _hoisted_3 = { class: "row mb-3" }
const _hoisted_4 = { class: "col-lg-6 col-md-9" }
const _hoisted_5 = { class: "container" }
const _hoisted_6 = { class: "row mb-2" }
const _hoisted_7 = { class: "col" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = {
  key: 0,
  class: "row mb-3 ps-3"
}
const _hoisted_11 = /*#__PURE__*/_createElementVNode("div", { class: "col text-danger" }, " You were automatically logged out, please sign in again ", -1)
const _hoisted_12 = [
  _hoisted_11
]
const _hoisted_13 = { class: "row mb-2 ps-3" }
const _hoisted_14 = { class: "col" }
const _hoisted_15 = {
  key: 1,
  class: "row mb-2 ps-3"
}
const _hoisted_16 = { class: "col" }
const _hoisted_17 = { class: "row mb-2 mt-4 ps-3" }
const _hoisted_18 = { class: "col pb-2" }
const _hoisted_19 = { class: "d-grid gap-2 col-5 mx-auto" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = {
  key: 2,
  class: "row mt-3 mb-2 ps-3"
}
const _hoisted_22 = { class: "col" }
const _hoisted_23 = { class: "text-danger text-center" }
const _hoisted_24 = {
  key: 3,
  class: "row mt-3 mb-2 ps-3"
}
const _hoisted_25 = { class: "col text-center" }
const _hoisted_26 = {
  key: 4,
  class: "row mt-3 mb-2 ps-3"
}
const _hoisted_27 = { class: "col text-center" }

import { ref, watch, computed, reactive, onBeforeMount } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// -- Own methods -- //
import {
  loginTroubleReport,
  getPasswordResetToken,
} from '@/aux/authentication';
import { validateEmail } from '../../techlog-shared/functions/inputValidation';
import errorHandler from '@/aux/errorHandler';
import { IformProperties, formEntriesComplete } from '@/aux/formHandling';
import { needToDownloadTerms, APP_SETTINGS } from '@/aux/app_configuration';

// -- Own Vue components -- //
import FormInput from '@/components/modals/FormInputV2.vue';
import TermsAgreement from '@/components/modals/TermsAgreement.vue';

// -- Import vue router -- //

export default /*#__PURE__*/_defineComponent({
  __name: 'LoginView',
  setup(__props) {

/**
 * Imports
 */
// -- Vue methods -- //
const router = useRouter();
const route = useRoute();

// -- Component variables -- //
const loginError = ref<string | null>(null);
const resetValidation = ref<number>(0);
const loginButtonActive = ref<boolean>(true);
const logoutForced = ref<string | null>(null);
const forgotPassword = ref<boolean>(false);
const showTerms = ref<boolean>(false);
const termsActualVersion = ref<number>(0);

const passwordRequired = computed(() => {
  return !forgotPassword.value;
});

const formProperties: IformProperties = reactive({
  userEmail: {
    value: '',
    inputIsValid: function () {
      return validateEmail(this.value);
    },
    options: {
      inputType: 'email',
      label: 'E-mail',
      errorText: 'Enter a valid e-mail address',
      requiredInput: true,
    },
  },
  userPassword: {
    value: '',
    inputIsValid: function () {
      return this.value.length > 0 || !this?.options?.requiredInput;
    },
    options: {
      inputType: 'password',
      label: 'Password',
      errorText: 'Enter a valid password',
      requiredInput: passwordRequired,
    },
  },
});

/**
 * Check if terms need to be downloaded
 */
onBeforeMount(async () => {
  if (!APP_SETTINGS('BYPASS_USER_TERMS')) {
    const result = await needToDownloadTerms();

    showTerms.value = result.showTerms;
    termsActualVersion.value = result.termsActualVersion;
  }
});

// -- Component computed functions --/
// Wrapper function
const formEntriesCompleteWrapper = computed(() => {
  return formEntriesComplete(formProperties);
});

// -- Component functions --//
const peformLogin = async () => {
  if (!formEntriesCompleteWrapper.value) {
    loginButtonActive.value = true;
    return;
  }

  const formData = {
    userEmail: formProperties.userEmail.value,
    userPassword: formProperties.userPassword.value,
  };

  // Perform login
  loginError.value = await loginTroubleReport(formData);
  if (!loginError.value) {
    // Redirect user to the path he/she is coming from
    const originPath: string = Array.isArray(route?.params?.originPath)
      ? route?.params?.originPath[0]
      : route?.params?.originPath;

    if (originPath) {
      return router.push({ path: originPath });
    }

    // If the user does not come from a certain origin: re-direct to the fleet overview
    // page
    return router.push({ name: 'fleet-overview' });
  }

  resetForm();
};

const performForgotPassword = async () => {
  if (!formEntriesCompleteWrapper.value) {
    loginButtonActive.value = true;
    return;
  }

  const userEmail = formProperties.userEmail.value;

  try {
    await getPasswordResetToken(userEmail);
    loginError.value = `Password reset link sent to ${userEmail}`;
  } catch (error) {
    loginError.value = errorHandler(error as Error);
  }

  resetForm();
};

const loginClick = () => {
  loginButtonActive.value = false;
  loginError.value = null;

  // Perform login action
  if (!forgotPassword.value) {
    peformLogin();
  }

  // Perform "forgot password" action
  if (forgotPassword.value) {
    performForgotPassword();
  }
};

/**
 * Reset the form for another attempt
 */
const resetForm = (): void => {
  formProperties.userPassword.value = '';
  loginButtonActive.value = true;

  resetValidation.value += 1;
};

/**
 * Toggle between login and forgot password screen
 */
const toggleLoginForgotPassword = (): void => {
  forgotPassword.value = !forgotPassword.value;
  loginError.value = '';
  resetForm();
};

/**
 * Verify if a logout was forced
 */
watch(
  () => route?.params?.forceLogout,
  () => {
    logoutForced.value = route?.params?.forceLogout as string;
  },
  {
    immediate: true,
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _hoisted_2,
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                (!forgotPassword.value)
                  ? (_openBlock(), _createElementBlock("h5", _hoisted_8, "Log in"))
                  : (_openBlock(), _createElementBlock("h5", _hoisted_9, "Forgot password"))
              ])
            ]),
            (logoutForced.value === 'true')
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, _hoisted_12))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(FormInput, _mergeProps({
                  value: formProperties.userEmail.value,
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((formProperties.userEmail.value) = $event)),
                  inputIsValid: formProperties.userEmail.inputIsValid?.()
                }, formProperties.userEmail.options, {
                  resetValidation: resetValidation.value,
                  onKeyup: _cache[1] || (_cache[1] = _withKeys(($event: any) => (loginClick()), ["enter"]))
                }), null, 16, ["value", "inputIsValid", "resetValidation"])
              ])
            ]),
            (!forgotPassword.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createVNode(FormInput, _mergeProps({
                      value: formProperties.userPassword.value,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((formProperties.userPassword.value) = $event)),
                      inputIsValid: formProperties.userPassword.inputIsValid?.()
                    }, formProperties.userPassword.options, {
                      resetValidation: resetValidation.value,
                      onKeyup: _cache[3] || (_cache[3] = _withKeys(($event: any) => (loginClick()), ["enter"]))
                    }), null, 16, ["value", "inputIsValid", "resetValidation"])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn btn-primary",
                    disabled: !loginButtonActive.value || !_unref(formEntriesCompleteWrapper),
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (loginClick()))
                  }, _toDisplayString(forgotPassword.value ? 'Reset password' : 'Log in'), 9, _hoisted_20)
                ])
              ])
            ]),
            (loginError.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("p", _hoisted_23, _toDisplayString(loginError.value), 1)
                  ])
                ]))
              : _createCommentVNode("", true),
            (!forgotPassword.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                  _createElementVNode("div", _hoisted_25, [
                    _createElementVNode("a", {
                      href: "#",
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (toggleLoginForgotPassword()))
                    }, " Forgot password? ")
                  ])
                ]))
              : _createCommentVNode("", true),
            (forgotPassword.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                  _createElementVNode("div", _hoisted_27, [
                    _createElementVNode("a", {
                      href: "#",
                      onClick: _cache[6] || (_cache[6] = ($event: any) => (toggleLoginForgotPassword()))
                    }, " Go back to login ")
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    (showTerms.value)
      ? (_openBlock(), _createBlock(TermsAgreement, {
          key: 0,
          termsVersion: termsActualVersion.value,
          onTermsAccepted: _cache[7] || (_cache[7] = ($event: any) => (showTerms.value = false))
        }, null, 8, ["termsVersion"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})